import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Skeleton } from "../../components/ui/Feedback";
import { Input } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";
import { LabelWithHint } from "../../components/ui/Tooltip";
import { formatMoney } from "../../lib/format";
import { majorToMinor, minorToMajor } from "../../lib/money";
import type { TierWithGifts } from "../../lib/types";
import { useCreateTier, useDeleteTier, useSaveTierCode, useTiers, useUpdateTier } from "./useTiers";

// Mirrors the storefront's defaults in app-shopify/storefront/src/codes.ts: a
// blank message means the default, so these are shown, never saved.
const DEFAULT_LOCKED = "Spend {remaining} more for {offer}";
const DEFAULT_UNLOCKED = "{offer} unlocked";

/** A new tier starts above the last one, so it never collides with it at publish. */
const NEXT_TIER_STEP_MINOR = 5000;
const EXAMPLE_REMAINING_MINOR = 1000;

const THRESHOLD_HELP =
	"The cart subtotal, after discounts and excluding gifts, that unlocks this tier. If the shopper drops back below it, the tier locks again. The code's own discount does not count against it, so applying the code never hides it.";

const CODE_HELP =
	"A discount code you have already created in Shopify, typed exactly as shoppers enter it. Tiers can share one code. Shopify applies it and enforces its own minimum at checkout. The code is readable in your storefront's page source, so the unlock is a nudge rather than a secret.";

const OFFER_HELP =
	"What this tier adds, as shoppers see it. For example: 20% off at the first tier, Free shipping at the next.";

const MESSAGE_HELP =
	"Leave blank for the default. Use {remaining} for the amount still to spend, {offer} for this tier's offer, {code} for the code and {threshold} for this tier's spend.";

const toUsd = (minor: number): string => formatMoney(minorToMajor(minor), "USD");

const fillExample = (
	template: string,
	values: { offer: string; code: string; thresholdMinor: number },
): string => {
	const tokens: Record<string, string> = {
		remaining: toUsd(EXAMPLE_REMAINING_MINOR),
		offer: values.offer || "your offer",
		code: values.code || "CODE",
		threshold: toUsd(values.thresholdMinor),
	};
	return template.replace(/\{(\w+)\}/g, (token, name: string) => tokens[name] ?? token);
};

type TierCodeCardProps = {
	campaignId: string;
	tier: TierWithGifts;
	position: number;
	/** The tier below's code, offered for a new tier since tiers usually share one. */
	suggestedCode: string;
	removable: boolean;
};

const TierCodeCard = ({
	campaignId,
	tier,
	position,
	suggestedCode,
	removable,
}: TierCodeCardProps) => {
	const updateTier = useUpdateTier(campaignId);
	const deleteTier = useDeleteTier(campaignId);
	const saveCode = useSaveTierCode(campaignId);
	const toast = useToast();
	const confirm = useConfirm();
	const [threshold, setThreshold] = useState(minorToMajor(tier.thresholdMinor));
	const [code, setCode] = useState(tier.code?.code ?? suggestedCode);
	const [offerLabel, setOfferLabel] = useState(tier.code?.offerLabel ?? "");
	const [lockedMessage, setLockedMessage] = useState(tier.code?.lockedMessage ?? "");
	const [unlockedMessage, setUnlockedMessage] = useState(tier.code?.unlockedMessage ?? "");

	const notify = { onError: (error: unknown) => toast.push((error as Error).message, "error") };

	const commitThreshold = () => {
		const thresholdMinor = majorToMinor(threshold || "0");
		if (thresholdMinor === tier.thresholdMinor) return;
		updateTier.mutate(
			{ tierId: tier.id, thresholdMinor },
			{ onSuccess: () => toast.push("Spend updated"), ...notify },
		);
	};

	// The code and offer are required, so nothing is sent until both are filled
	// in: saving either alone would be refused and read as the merchant's error.
	const commitCode = () => {
		const next = {
			code: code.trim(),
			offerLabel: offerLabel.trim(),
			lockedMessage: lockedMessage.trim() || null,
			unlockedMessage: unlockedMessage.trim() || null,
		};
		if (!next.code || !next.offerLabel) return;
		const saved = tier.code;
		if (
			saved &&
			saved.code === next.code &&
			saved.offerLabel === next.offerLabel &&
			saved.lockedMessage === next.lockedMessage &&
			saved.unlockedMessage === next.unlockedMessage
		) {
			return;
		}
		saveCode.mutate(
			{ tierId: tier.id, ...next },
			{ onSuccess: () => toast.push("Tier saved"), ...notify },
		);
	};

	const example = {
		offer: offerLabel.trim(),
		code: code.trim(),
		thresholdMinor: majorToMinor(threshold || "0"),
	};

	return (
		<div className="rounded-md border border-line">
			<div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
				<span className="text-xs font-medium text-ink">Tier {position}</span>
				<Button
					variant="ghost"
					size="sm"
					aria-label={`Delete tier ${position}`}
					disabled={!removable}
					onClick={() =>
						confirm({
							title: `Delete tier ${position}?`,
							description: "Its code, offer and messages will be removed.",
							onConfirm: () =>
								deleteTier.mutate(tier.id, {
									onSuccess: () => toast.push("Tier deleted"),
									...notify,
								}),
						})
					}
				>
					<Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
				</Button>
			</div>

			<div className="grid gap-4 p-4 sm:grid-cols-2">
				<label className="block space-y-1.5">
					<LabelWithHint label="Unlocks at">{THRESHOLD_HELP}</LabelWithHint>
					<Input
						type="number"
						min="0"
						step="0.01"
						value={threshold}
						onChange={(event) => setThreshold(event.target.value)}
						onBlur={commitThreshold}
					/>
				</label>
				<label className="block space-y-1.5">
					<LabelWithHint label="Discount code">{CODE_HELP}</LabelWithHint>
					<Input
						value={code}
						onChange={(event) => setCode(event.target.value)}
						onBlur={commitCode}
						placeholder="SAVE20"
						autoComplete="off"
						spellCheck={false}
					/>
				</label>
				<label className="block space-y-1.5 sm:col-span-2">
					<LabelWithHint label="Offer">{OFFER_HELP}</LabelWithHint>
					<Input
						value={offerLabel}
						onChange={(event) => setOfferLabel(event.target.value)}
						onBlur={commitCode}
						placeholder={position === 1 ? "20% off" : "Free shipping"}
					/>
				</label>
				<label className="block space-y-1.5">
					<LabelWithHint label="Before it unlocks">{MESSAGE_HELP}</LabelWithHint>
					<Input
						value={lockedMessage}
						onChange={(event) => setLockedMessage(event.target.value)}
						onBlur={commitCode}
						placeholder={DEFAULT_LOCKED}
					/>
					<span className="block text-xs text-ink-muted">
						{fillExample(lockedMessage.trim() || DEFAULT_LOCKED, example)}
					</span>
				</label>
				<label className="block space-y-1.5">
					<LabelWithHint label="Once unlocked">{MESSAGE_HELP}</LabelWithHint>
					<Input
						value={unlockedMessage}
						onChange={(event) => setUnlockedMessage(event.target.value)}
						onBlur={commitCode}
						placeholder={DEFAULT_UNLOCKED}
					/>
					<span className="block text-xs text-ink-muted">
						{fillExample(unlockedMessage.trim() || DEFAULT_UNLOCKED, example)}
					</span>
				</label>
			</div>
		</div>
	);
};

export const CodeEditor = ({ campaignId }: { campaignId: string }) => {
	const tiers = useTiers(campaignId);
	const createTier = useCreateTier(campaignId);
	const toast = useToast();

	if (tiers.isPending) return <Skeleton className="h-48 w-full" />;

	const sorted = [...(tiers.data?.items ?? [])].sort(
		(left, right) => left.thresholdMinor - right.thresholdMinor,
	);
	const top = sorted[sorted.length - 1];

	const addTier = () =>
		createTier.mutate((top?.thresholdMinor ?? 0) + NEXT_TIER_STEP_MINOR, {
			onSuccess: () => toast.push("Tier added"),
			onError: (error) => toast.push(error.message, "error"),
		});

	return (
		<div className="space-y-4">
			<p className="text-sm text-ink-muted">
				Each tier unlocks at a spend and reveals a code. Shoppers see one progress bar with a step
				for every tier, and the code of the highest tier they have reached.
			</p>

			{sorted.map((tier, index) => (
				<TierCodeCard
					key={tier.id}
					campaignId={campaignId}
					tier={tier}
					position={index + 1}
					suggestedCode={sorted[index - 1]?.code?.code ?? ""}
					removable={sorted.length > 1}
				/>
			))}

			<Button variant="outline" size="sm" onClick={addTier} disabled={createTier.isPending}>
				<Plus className="h-3.5 w-3.5" strokeWidth={2} />
				Add tier
			</Button>
		</div>
	);
};
